import { useMemo } from "react";
import moment from "moment";
import Chart from "./Chart";
import config from "../../constants/charts";
import { defaultChartOptions } from "../../helpers/charts";
import { rgbToRgba } from "../../helpers";
import { useMetricsState } from "../../context/metrics";
import { Chart as ChartJS, ChartOptions } from "chart.js";

import { getChartConfig } from "../../helpers/getChartConfig";
import { useChartData } from "../../data/useChartData";
import { getPercentageInThresholds, VPDThreshold } from "./thresholds";
import { DateRange } from "../../api/agritech";
import { Theme } from "@material-ui/core";

export const thresholdConfig = (
  label: string,
  threshold: number,
  color: {
    main: string;
    light: string;
    dark: string;
  },
  fill: "start" | "end" | "stack",
  range: DateRange
) => ({
  label: label,
  type: "line",
  data: Object.values(range).map((time) => ({
    x: moment(time),
    y: threshold,
  })),
  fill,
  borderWidth: 2,
  backgroundColor: rgbToRgba(color.light, 0.6),
  pointBackgroundColor: rgbToRgba(color.light, 0.6),
  borderColor: rgbToRgba(color.main, 1),
  pointBorderColor: rgbToRgba(color.main, 1),
  pointRadius: 0,
  borderDash: [10, 5],
});

type ChartScale = NonNullable<ChartOptions<"line">["scales"]>[string];
const lightAxis: ChartScale = {
  type: "linear",
  display: true,
  position: "right",
  title: {
    display: true,
    text: "J/m²/sec",
  },
  beginAtZero: true,
  grid: {
    borderDash: [4, 10],
    drawOnChartArea: false,
  },
};
const windAxis: ChartScale = {
  type: "linear",
  display: true,
  position: "right",
  title: {
    display: true,
    text: "km/h",
  },
  beginAtZero: true,
  grid: {
    borderDash: [4, 10],
    drawOnChartArea: false,
  },
};
const ChartAdvisoryLine = ({
  chart,
  fieldId,
  chartEvents,
  range,
  theme,
  thresholds,
  dataKey = "vpd",
}: {
  chart: { id: keyof typeof config; group: string };
  fieldId: number;
  range: DateRange;
  theme: Theme;
  chartEvents: {
    chartRef: unknown;
  };
  thresholds: Record<string, VPDThreshold>;
  dataKey?: string;
}) => {
  const { chartData } = useChartData(chart.group, range, fieldId);

  const metrics = useMetricsState();
  const datasetsPresent = useMemo(() => {
    return {
      light: chartData?.some(
        (d: Record<string, unknown>) => d.light !== undefined
      ),
      wind: chartData?.some(
        (d: Record<string, unknown>) => d.wind_speed !== undefined
      ),
    };
  }, [chartData]);
  const chartConfig = useMemo(() => {
    const axis = [{ yAxisID: "y" }, { yAxisID: "y2" }, { yAxisID: "y3" }];
    const initialConfig = getChartConfig(chartData, {
      chartType: chart.id,
      theme,
      metrics,
    });
    const advisoryLines = Object.values(thresholds).map(
      ({ label, value, color, fill }) =>
        thresholdConfig(label, value, color, fill, range)
    );
    const black = theme.palette.common.black;
    return {
      ...initialConfig,
      datasets: [
        ...initialConfig.datasets.map((item, i) => {
          const isMainLine = item.property === dataKey;
          return {
            ...item,
            ...axis[i],
            ...(isMainLine && {
              borderColor: black,
              pointBorderColor: black,
              pointBackgroundColor: black,
            }),
          };
        }),
        ...advisoryLines,
      ],
    };
  }, [chartData, theme, chart.id, thresholds, metrics]);

  const thresholdInfo = useMemo(() => {
    return getPercentageInThresholds(
      (chartData ?? []) as ({ timestamp: number } & Record<string, number>)[],
      thresholds,
      dataKey
    );
  }, [chartData, thresholds, dataKey]);

  const chartOptions = useMemo(() => {
    const options = defaultChartOptions(
      config[chart.id].axis,
      config[chart.id].legend,
      range,
      chartEvents
    );

    // note: using any here since options.scale is legacy type that is hard to satisfy
    let scales: any = options.scales;
    if (datasetsPresent.light && datasetsPresent.wind) {
      scales = {
        ...options.scales,
        y2: lightAxis,
        y3: { ...windAxis, display: false },
      };
    } else if (datasetsPresent.light) {
      scales = {
        ...options.scales,
        y2: lightAxis,
      };
    } else if (datasetsPresent.wind) {
      scales = {
        ...options.scales,
        y2: windAxis,
      };
    }

    return {
      ...options,
      scales,
      plugins: {
        ...options.plugins,
        legend: {
          display: true,
          position: "bottom",
          labels: {
            generateLabels: (chartInstance: ChartJS) => {
              // Get the default dataset legend items
              const defaultLabels =
                ChartJS.defaults.plugins.legend.labels.generateLabels(
                  chartInstance
                );

              const withExtraInfo = defaultLabels.map((l) => {
                const matchingThreshold = thresholdInfo.find(
                  (t) => t[0].label === l.text
                );
                if (!matchingThreshold) {
                  // return label, but with transparent fill
                  return {
                    ...l,
                    fillStyle: "transparent",
                  };
                }
                return {
                  ...l,
                  text: `${l.text} (${matchingThreshold[1].toFixed(0)}%)`,
                };
              });
              // put threshold labels at the start
              withExtraInfo.sort((a, b) => {
                const af = +(a.fillStyle === "transparent");
                const bf = +(b.fillStyle === "transparent");
                return af - bf;
              });
              return withExtraInfo;
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem: {
              dataset: { label: string };
              formattedValue: string;
            }) => {
              return (
                tooltipItem.dataset.label + ": " + tooltipItem.formattedValue
              );
            },
          },
        },
      },
    };
  }, [chart.id, range, thresholdInfo, datasetsPresent]);

  return (
    <Chart
      chartRef={chartEvents.chartRef}
      data={chartConfig}
      options={chartOptions}
    />
  );
};

export default ChartAdvisoryLine;
